'use client'

/**
 * Thread-Based Engineering Diagrams
 * Visual components for the thread-based engineering blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A
 */

// Base Thread Diagram
export function BaseThreadDiagram() {
  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">THE BASE THREAD</h3>
        <p className="text-sm text-muted-foreground">
          A unit of engineering work driven by <span className="font-semibold text-foreground">you</span> and your agent
        </p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Main flow */}
        <div className="flex items-center justify-center gap-4 md:gap-6 w-full">
          {/* Prompt Node */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#008dcc] flex items-center justify-center">
              <span className="text-xl md:text-2xl font-bold text-white">P</span>
            </div>
            <span className="text-xs text-muted-foreground">PROMPT</span>
            <span className="text-[10px] text-[#008dcc]">You define</span>
          </div>

          {/* Arrow */}
          <div className="w-6 md:w-8 h-0.5 bg-gray-500 relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-gray-500" />
          </div>

          {/* Agent Work Box */}
          <div className="border-2 border-[#008dcc]/50 rounded-xl p-4 md:p-5 bg-[#008dcc]/5">
            <p className="text-xs md:text-sm font-bold text-foreground text-center mb-3">AGENT EXECUTES</p>
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#008dcc]/20 border border-[#008dcc]/40 flex items-center justify-center">
                  <span className="text-[10px] md:text-xs font-medium text-[#008dcc]">T{i}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground text-center mt-2">Tool calls (automated)</p>
          </div>

          {/* Arrow */}
          <div className="w-6 md:w-8 h-0.5 bg-gray-500 relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-gray-500" />
          </div>

          {/* Review Node */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-emerald-500 flex items-center justify-center">
              <span className="text-xl md:text-2xl font-bold text-white">R</span>
            </div>
            <span className="text-xs text-muted-foreground">REVIEW</span>
            <span className="text-[10px] text-emerald-500">You verify</span>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">You show up twice:</span> beginning (prompt) and end (review). Everything between is automated.
          </p>
        </div>
      </div>
    </div>
  )
}

// P-Threads (Parallel) Diagram
export function PThreadsDiagram() {
  const threads = [
    { label: 'Auth', color: '#008dcc' },
    { label: 'API', color: '#22c55e' },
    { label: 'Tests', color: '#FDC304' },
  ]

  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">P-THREADS: PARALLEL EXECUTION</h3>
        <p className="text-sm text-muted-foreground">Multiple independent threads running simultaneously</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Parallel threads visualization */}
        <div className="flex flex-col gap-3 w-full max-w-md">
          {threads.map((thread, idx) => (
            <div key={idx} className="flex items-center justify-center gap-3 md:gap-4">
              {/* Prompt */}
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: thread.color }}>
                <span className="text-sm md:text-base font-bold text-white">P</span>
              </div>

              {/* Task label + Arrow */}
              <div className="flex-1 flex items-center gap-2">
                <span className="text-[10px] md:text-xs font-medium px-2 py-1 rounded text-white"
                  style={{ backgroundColor: thread.color }}>
                  {thread.label}
                </span>
                <div className="flex-1 h-0.5 relative" style={{ backgroundColor: thread.color }}>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent"
                    style={{ borderLeftColor: thread.color }} />
                </div>
              </div>

              {/* Review */}
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <span className="text-sm md:text-base font-bold text-white">R</span>
              </div>
            </div>
          ))}
        </div>

        {/* Simultaneous indicator */}
        <div className="flex items-center gap-2 text-muted-foreground">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2v20M2 12h20" strokeLinecap="round" />
          </svg>
          <span className="text-xs">All running at the same time</span>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">10-15 parallel instances</span> across terminal tabs and interfaces. Context-switch between reviews, not execution.
          </p>
        </div>
      </div>
    </div>
  )
}

// C-Threads (Chained/Phased) Diagram
export function CThreadsDiagram() {
  const phases = [
    { name: 'Plan', desc: 'Architecture' },
    { name: 'Build', desc: 'Implementation' },
    { name: 'Deploy', desc: 'Production' },
  ]

  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">C-THREADS: CHAINED PHASES</h3>
        <p className="text-sm text-muted-foreground">Human checkpoint between each phase catches errors early</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Phases visualization */}
        <div className="flex items-center justify-center gap-1 md:gap-2 flex-wrap">
          {phases.map((phase, idx) => (
            <div key={idx} className="flex items-center">
              {/* Phase block */}
              <div className="flex flex-col items-center">
                <div className="border-2 border-[#22c55e] rounded-lg p-2 md:p-3 bg-[#22c55e]/5 min-w-[80px] md:min-w-[100px]">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-[#008dcc] flex items-center justify-center">
                      <span className="text-[10px] md:text-xs font-bold text-white">P</span>
                    </div>
                    <svg className="w-3 h-3 text-gray-500" viewBox="0 0 24 24" fill="none">
                      <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                      <span className="text-[10px] md:text-xs font-bold text-white">R</span>
                    </div>
                  </div>
                  <p className="text-[10px] md:text-xs font-semibold text-foreground text-center">{phase.name}</p>
                  <p className="text-[8px] md:text-[10px] text-muted-foreground text-center">{phase.desc}</p>
                </div>
                <span className="text-[10px] text-[#22c55e] mt-1">Phase {idx + 1}</span>
              </div>

              {/* Connector arrow */}
              {idx < phases.length - 1 && (
                <div className="flex items-center px-1 md:px-2">
                  <div className="w-4 md:w-8 h-0.5 bg-[#22c55e] relative">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-[#22c55e]" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#22c55e]/10 rounded-lg border border-[#22c55e]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Review after each phase</span> — output from Phase 1 becomes input for Phase 2. Catch errors before they compound.
          </p>
        </div>
      </div>
    </div>
  )
}

// L-Threads (Long Duration) Diagram
export function LThreadsDiagram() {
  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">L-THREADS: LONG DURATION</h3>
        <p className="text-sm text-muted-foreground">Extended autonomy spanning hours without intervention</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Main flow */}
        <div className="flex items-center justify-center gap-3 md:gap-4 w-full">
          {/* Prompt */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#008dcc] flex items-center justify-center flex-shrink-0">
              <span className="text-lg md:text-xl font-bold text-white">P</span>
            </div>
            <span className="text-xs text-muted-foreground">START</span>
          </div>

          {/* Long execution timeline */}
          <div className="flex-1 max-w-sm">
            <div className="border-2 border-[#008dcc]/40 rounded-xl p-3 md:p-4 bg-[#008dcc]/5">
              {/* Progress dots */}
              <div className="flex items-center justify-between mb-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#008dcc]"
                    style={{ opacity: 0.3 + (i * 0.1) }} />
                ))}
              </div>

              {/* Step count */}
              <div className="text-center">
                <p className="text-lg md:text-xl font-bold text-[#008dcc]">100+ steps</p>
                <p className="text-[10px] md:text-xs text-muted-foreground">Hours of autonomous work</p>
              </div>

              {/* No intervention indicator */}
              <div className="flex items-center justify-center gap-1 mt-2 text-muted-foreground">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M4.93 4.93l14.14 14.14" />
                </svg>
                <span className="text-[10px]">No human checkpoints</span>
              </div>
            </div>
          </div>

          {/* Review */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-lg md:text-xl font-bold text-white">R</span>
            </div>
            <span className="text-xs text-muted-foreground">END</span>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Requires trust built over time.</span> Start with shorter threads, extend duration as verification improves.
          </p>
        </div>
      </div>
    </div>
  )
}

// F-Threads (Fusion) Diagram
export function FThreadsDiagram() {
  const agents = [
    { name: 'Agent A', result: 'Option 1' },
    { name: 'Agent B', result: 'Option 2' },
    { name: 'Agent C', result: 'Option 3' },
  ]

  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">F-THREADS: FUSION & SELECTION</h3>
        <p className="text-sm text-muted-foreground">Same prompt to multiple agents, select the best result</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Main visualization */}
        <div className="flex items-center justify-center gap-2 md:gap-4">
          {/* Single prompt fans out */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#FDC304] flex items-center justify-center">
              <span className="text-lg md:text-xl font-bold text-black">P</span>
            </div>
            <span className="text-[10px] text-muted-foreground">Same prompt</span>
          </div>

          {/* Fan out arrows */}
          <div className="flex flex-col gap-2 py-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-6 md:w-8 h-0.5 bg-[#FDC304] relative"
                style={{ transform: `rotate(${(i - 1) * 15}deg)` }}>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-2 border-b-2 border-l-4 border-transparent border-l-[#FDC304]" />
              </div>
            ))}
          </div>

          {/* Multiple agents */}
          <div className="flex flex-col gap-2">
            {agents.map((agent, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="px-2 py-1 rounded bg-[#FDC304]/20 border border-[#FDC304]/40">
                  <span className="text-[10px] md:text-xs font-medium text-foreground">{agent.name}</span>
                </div>
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-emerald-500/80 flex items-center justify-center">
                  <span className="text-[10px] md:text-xs font-bold text-white">R{i + 1}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Converge arrows */}
          <div className="flex flex-col gap-2 py-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-6 md:w-8 h-0.5 bg-[#FDC304] relative"
                style={{ transform: `rotate(${(1 - i) * 15}deg)` }}>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-2 border-b-2 border-l-4 border-transparent border-l-[#FDC304]" />
              </div>
            ))}
          </div>

          {/* Fusion selector */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FDC304] rounded-lg rotate-45 flex items-center justify-center">
              <div className="-rotate-45 text-center">
                <span className="text-xs md:text-sm font-bold text-black">BEST</span>
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground">Select winner</span>
          </div>

          {/* Arrow to final */}
          <div className="w-6 md:w-8 h-0.5 bg-emerald-500 relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-emerald-500" />
          </div>

          {/* Final result */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-emerald-500 flex items-center justify-center ring-4 ring-emerald-500/30">
              <span className="text-lg md:text-xl font-bold text-white">✓</span>
            </div>
            <span className="text-[10px] text-emerald-500">Best result</span>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#FDC304]/10 rounded-lg border border-[#FDC304]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Multiple perspectives on one problem.</span> Best for architecture decisions, design choices, or when you want diverse solutions.
          </p>
        </div>
      </div>
    </div>
  )
}

// B-Threads (Big/Sub-agent) Diagram
export function BThreadsDiagram() {
  const subThreads = [
    { task: 'Database schema', status: 'done' },
    { task: 'API endpoints', status: 'done' },
    { task: 'Auth logic', status: 'running' },
    { task: 'Unit tests', status: 'pending' },
  ]

  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">B-THREADS: SUB-AGENT ORCHESTRATION</h3>
        <p className="text-sm text-muted-foreground">Main agent spawns and coordinates sub-agents</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Main flow */}
        <div className="flex items-center justify-center gap-3 md:gap-4 w-full">
          {/* Main Prompt */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#F90B8A] flex items-center justify-center flex-shrink-0">
              <span className="text-lg md:text-xl font-bold text-white">P</span>
            </div>
            <span className="text-[10px] text-muted-foreground">Main task</span>
          </div>

          {/* Arrow */}
          <div className="w-4 md:w-6 h-0.5 bg-[#F90B8A] relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-[#F90B8A]" />
          </div>

          {/* Orchestrator + Sub-threads */}
          <div className="flex-1 max-w-sm">
            <div className="border-2 border-[#F90B8A] rounded-xl p-3 md:p-4 bg-[#F90B8A]/5">
              {/* Orchestrator label */}
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-[#F90B8A] flex items-center justify-center">
                  <span className="text-xs font-bold text-white">O</span>
                </div>
                <span className="text-xs md:text-sm font-bold text-foreground">ORCHESTRATOR</span>
              </div>

              {/* Sub-threads */}
              <div className="flex flex-col gap-2">
                {subThreads.map((thread, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#008dcc]/80 flex items-center justify-center">
                      <span className="text-[8px] md:text-[10px] font-bold text-white">S{i + 1}</span>
                    </div>
                    <span className="text-[10px] md:text-xs text-foreground flex-1">{thread.task}</span>
                    <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center ${
                      thread.status === 'done' ? 'bg-emerald-500' :
                      thread.status === 'running' ? 'bg-[#FDC304]' : 'bg-gray-400'
                    }`}>
                      {thread.status === 'done' && <span className="text-[8px] text-white">✓</span>}
                      {thread.status === 'running' && <span className="text-[8px] text-black">●</span>}
                      {thread.status === 'pending' && <span className="text-[8px] text-white">○</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="w-4 md:w-6 h-0.5 bg-[#F90B8A] relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-[#F90B8A]" />
          </div>

          {/* Main Review */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-lg md:text-xl font-bold text-white">R</span>
            </div>
            <span className="text-[10px] text-muted-foreground">Final review</span>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#F90B8A]/10 rounded-lg border border-[#F90B8A]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Nested delegation.</span> One prompt generates multiple sub-prompts. Multiply throughput without proportional effort increase.
          </p>
        </div>
      </div>
    </div>
  )
}

// Z-Threads (Zero-Touch) Diagram
export function ZThreadsDiagram() {
  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">Z-THREADS: ZERO-TOUCH AUTONOMOUS</h3>
        <p className="text-sm text-muted-foreground">Self-verifying execution with optional human escalation</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Main flow */}
        <div className="flex items-center justify-center gap-4 md:gap-6 w-full">
          {/* Prompt */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#8b5cf6] flex items-center justify-center flex-shrink-0">
              <span className="text-xl md:text-2xl font-bold text-white">P</span>
            </div>
            <span className="text-xs text-muted-foreground">PROMPT</span>
          </div>

          {/* Arrow to agent */}
          <div className="w-6 md:w-8 h-0.5 bg-gray-500 relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-gray-500" />
          </div>

          {/* Agent Work with Self-Verification Loop */}
          <div className="relative">
            <div className="border-2 border-[#8b5cf6] rounded-xl p-4 md:p-5 bg-[#8b5cf6]/10">
              <p className="text-xs md:text-sm font-bold text-foreground text-center mb-3">AUTONOMOUS AGENT</p>

              {/* Self-verification cycle */}
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#8b5cf6] flex items-center justify-center">
                    <span className="text-[10px] md:text-xs font-bold text-white">DO</span>
                  </div>
                </div>
                <svg className="w-6 h-6 text-[#8b5cf6]" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                    <span className="text-[10px] md:text-xs font-bold text-white">✓</span>
                  </div>
                </div>
              </div>

              {/* Loop arrow */}
              <div className="flex justify-center">
                <svg className="w-16 h-6 text-[#8b5cf6]/60" viewBox="0 0 64 24" fill="none">
                  <path d="M56 6c0 8-16 12-24 12S8 14 8 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M4 8l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="text-[10px] text-muted-foreground text-center">SELF-VERIFY LOOP</p>
            </div>
          </div>

          {/* Arrow to completion */}
          <div className="w-6 md:w-8 h-0.5 bg-emerald-500 relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-emerald-500" />
          </div>

          {/* Auto-Complete (no human needed) */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 ring-4 ring-emerald-500/30">
              <span className="text-lg md:text-xl font-bold text-white">✓</span>
            </div>
            <span className="text-xs text-muted-foreground">AUTO-DONE</span>
          </div>
        </div>

        {/* Escalation path (optional) */}
        <div className="flex items-center gap-3 opacity-60">
          <div className="h-6 w-0.5 bg-gray-500/50" />
          <div className="flex items-center gap-2">
            <span className="text-[10px] md:text-xs text-muted-foreground">Edge case?</span>
            <div className="w-8 md:w-12 h-0.5 bg-gray-500/50 border-dashed relative">
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-2 border-b-2 border-l-4 border-transparent border-l-gray-500/50" />
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-dashed border-[#FDC304] bg-[#FDC304]/10 flex items-center justify-center">
              <span className="text-sm md:text-base font-bold text-[#FDC304]">R</span>
            </div>
            <span className="text-[10px] md:text-xs text-muted-foreground">(only if needed)</span>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#8b5cf6]/10 rounded-lg border border-[#8b5cf6]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Human review → Exception handling</span>, not mandatory checkpoint
          </p>
        </div>
      </div>
    </div>
  )
}

// The Core Four Diagram
export function CoreFourDiagram() {
  const fundamentals = [
    { name: 'Context', desc: 'Codebase, docs, history', icon: '📚', color: '#008dcc' },
    { name: 'Model', desc: 'Capability vs speed', icon: '🧠', color: '#22c55e' },
    { name: 'Prompt', desc: 'Clarity & constraints', icon: '✍️', color: '#FDC304' },
    { name: 'Tools', desc: 'File, CLI, API access', icon: '🔧', color: '#F90B8A' },
  ]

  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">THE CORE FOUR</h3>
        <p className="text-sm text-muted-foreground">Improving any one improves all four optimization dimensions</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Four fundamentals in a grid */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 w-full max-w-md">
          {fundamentals.map((item, idx) => (
            <div
              key={idx}
              className="relative p-3 md:p-4 rounded-xl border-2 bg-background"
              style={{ borderColor: item.color }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg md:text-xl">{item.icon}</span>
                <span className="font-bold text-sm md:text-base text-foreground">{item.name}</span>
              </div>
              <p className="text-[10px] md:text-xs text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Center connection visualization */}
        <div className="relative w-full max-w-md">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#008dcc]/20 via-[#FDC304]/20 to-[#F90B8A]/20 flex items-center justify-center border border-border/60">
              <span className="text-[10px] md:text-xs font-semibold text-foreground text-center leading-tight">ALL<br/>FOUR</span>
            </div>
          </div>
          {/* Connection lines */}
          <svg className="w-full h-8 md:h-10" viewBox="0 0 200 40">
            <path d="M50 0 L100 35 L150 0" stroke="#008dcc" strokeWidth="1" fill="none" opacity="0.3" />
            <path d="M50 40 L100 5 L150 40" stroke="#F90B8A" strokeWidth="1" fill="none" opacity="0.3" />
          </svg>
        </div>

        {/* Arrow down to result */}
        <div className="flex flex-col items-center gap-2">
          <svg className="w-6 h-8 text-muted-foreground" viewBox="0 0 24 32" fill="none">
            <path d="M12 0 L12 24 M6 18 L12 26 L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-xs md:text-sm font-semibold text-emerald-600 dark:text-emerald-400">All 4 Dimensions Improve</p>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Better context = longer threads.</span> Better prompts = fewer checkpoints. Better tools = more parallel work.
          </p>
        </div>
      </div>
    </div>
  )
}

// Four Dimensions of Thread Optimization Diagram
export function FourDimensionsDiagram() {
  const dimensions = [
    { name: 'MORE', subtitle: 'Threads', desc: 'Add terminal tabs, batch tasks', icon: '×', color: '#008dcc' },
    { name: 'LONGER', subtitle: 'Duration', desc: 'Extend autonomous work', icon: '→', color: '#22c55e' },
    { name: 'THICKER', subtitle: 'Sub-agents', desc: 'Nest agents within agents', icon: '⊕', color: '#F90B8A' },
    { name: 'FEWER', subtitle: 'Checkpoints', desc: 'Reduce human reviews', icon: '−', color: '#8b5cf6' },
  ]

  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">FOUR DIMENSIONS OF OPTIMIZATION</h3>
        <p className="text-sm text-muted-foreground">Exactly four ways to increase AI-assisted output</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        {/* Four dimensions visualization */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 w-full max-w-lg">
          {dimensions.map((dim, idx) => (
            <div
              key={idx}
              className="p-4 md:p-5 rounded-xl border-2 bg-background relative overflow-hidden"
              style={{ borderColor: dim.color }}
            >
              {/* Background icon */}
              <div
                className="absolute top-2 right-2 text-4xl md:text-5xl font-bold opacity-10"
                style={{ color: dim.color }}
              >
                {dim.icon}
              </div>

              <div className="relative z-10">
                <p className="text-lg md:text-xl font-bold" style={{ color: dim.color }}>{dim.name}</p>
                <p className="text-xs md:text-sm font-semibold text-foreground mb-1">{dim.subtitle}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground">{dim.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Center multiplier */}
        <div className="flex items-center justify-center gap-2">
          {dimensions.map((dim, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div
                className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${dim.color}20`, border: `2px solid ${dim.color}` }}
              >
                <span className="text-xs md:text-sm font-bold" style={{ color: dim.color }}>{dim.icon}</span>
              </div>
              {idx < dimensions.length - 1 && (
                <span className="text-lg md:text-xl font-bold text-muted-foreground">×</span>
              )}
            </div>
          ))}
          <span className="text-lg md:text-xl font-bold text-muted-foreground ml-2">=</span>
          <div className="ml-2 px-3 py-2 rounded-lg bg-emerald-500 text-white">
            <span className="text-sm md:text-base font-bold">OUTPUT</span>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#8b5cf6]/10 rounded-lg border border-[#8b5cf6]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Multiplicative effect.</span> Improving one dimension amplifies the others. 2× more threads + 2× longer = 4× output.
          </p>
        </div>
      </div>
    </div>
  )
}

// Implementation Path Diagram
export function ImplementationPathDiagram() {
  const weeks = [
    { week: 1, title: 'Base Threads', tasks: ['Single AI sessions', 'Verify every result', 'Build trust'], color: '#008dcc', thread: 'Base' },
    { week: 2, title: 'Add Parallel', tasks: ['Two agents at once', 'Handle context-switch', 'Batch similar work'], color: '#22c55e', thread: 'P' },
    { week: 3, title: 'Test-Driven', tasks: ['Write tests first', 'AI implements code', 'Auto-verification'], color: '#FDC304', thread: 'C' },
    { week: 4, title: 'Long Duration', tasks: ['First L-thread', 'Hours of autonomy', 'Proper checkpoints'], color: '#F90B8A', thread: 'L' },
  ]

  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">IMPLEMENTATION PATH</h3>
        <p className="text-sm text-muted-foreground">A 4-week progression from basic to advanced threads</p>
      </div>

      <div className="flex flex-col items-center gap-4">
        {/* Timeline visualization */}
        <div className="relative w-full max-w-lg">
          {/* Progress line */}
          <div className="absolute top-6 left-8 right-8 h-1 bg-gradient-to-r from-[#008dcc] via-[#22c55e] via-[#FDC304] to-[#F90B8A] rounded-full opacity-30" />

          {/* Week nodes */}
          <div className="flex justify-between relative z-10">
            {weeks.map((w, idx) => (
              <div key={idx} className="flex flex-col items-center">
                {/* Week circle */}
                <div
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border-2"
                  style={{ backgroundColor: `${w.color}20`, borderColor: w.color }}
                >
                  <span className="text-sm md:text-base font-bold" style={{ color: w.color }}>{w.thread}</span>
                </div>
                <span className="text-[10px] md:text-xs font-semibold text-muted-foreground mt-2">Week {w.week}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Week details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 w-full">
          {weeks.map((w, idx) => (
            <div
              key={idx}
              className="p-3 rounded-lg border bg-background"
              style={{ borderColor: `${w.color}40` }}
            >
              <p className="text-xs md:text-sm font-bold text-foreground mb-1">{w.title}</p>
              <ul className="space-y-0.5">
                {w.tasks.map((task, i) => (
                  <li key={i} className="text-[9px] md:text-[10px] text-muted-foreground flex items-start gap-1">
                    <span style={{ color: w.color }}>•</span>
                    {task}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Destination indicator */}
        <div className="flex items-center gap-3 mt-2">
          <div className="w-8 md:w-12 h-0.5 bg-[#8b5cf6]/50" />
          <div className="px-3 py-2 rounded-lg bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 flex items-center gap-2">
            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#8b5cf6] flex items-center justify-center">
              <span className="text-[10px] md:text-xs font-bold text-white">Z</span>
            </div>
            <span className="text-xs md:text-sm font-semibold text-foreground">Z-Threads (Destination)</span>
          </div>
        </div>

        {/* Key insight */}
        <div className="mt-2 px-4 py-2 bg-[#22c55e]/10 rounded-lg border border-[#22c55e]/30">
          <p className="text-xs md:text-sm text-center text-foreground">
            <span className="font-semibold">Don't skip stages.</span> Trust builds incrementally. Each week's success enables the next level of autonomy.
          </p>
        </div>
      </div>
    </div>
  )
}

// All Thread Types Overview
export function ThreadTypesOverview() {
  return (
    <div data-blog-layout="narrow" className="my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">THREAD TYPES AT A GLANCE</h3>
        <p className="text-sm text-muted-foreground">Choose the right thread for your task</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { name: 'Base', desc: 'Single prompt → review', color: '#008dcc' },
          { name: 'P-Thread', desc: 'Parallel execution', color: '#008dcc' },
          { name: 'L-Thread', desc: 'Long autonomous runs', color: '#008dcc' },
          { name: 'C-Thread', desc: 'Phased with checkpoints', color: '#22c55e' },
          { name: 'F-Thread', desc: 'Fusion & selection', color: '#FDC304' },
          { name: 'B-Thread', desc: 'Sub-agent orchestration', color: '#F90B8A' },
          { name: 'Z-Thread', desc: 'Zero-touch autonomous', color: '#8b5cf6' },
        ].map((thread) => (
          <div
            key={thread.name}
            className="p-4 rounded-lg border border-border/60 bg-background"
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
              style={{ backgroundColor: `${thread.color}20`, border: `2px solid ${thread.color}` }}
            >
              <span className="text-sm font-bold" style={{ color: thread.color }}>
                {thread.name.charAt(0)}
              </span>
            </div>
            <p className="font-semibold text-foreground text-sm">{thread.name}</p>
            <p className="text-xs text-muted-foreground">{thread.desc}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
